import { FilterPopoverTabs } from '@/Enums/Pixels/FilterPopoverTabs.enum';
import { IClientControlOption, IControlOption, IWhitelabelControlOption } from '@/Interfaces/ControlOption.interface';
import AddFilterButton from '@/components/Filter/AddFilterButton';
import { AdvertisersTabButton, ClientsTabButton, WhitelabelsTabButton } from '@/components/Filter/TabButtons';
import TabFilterPopover from '@/components/Filter/TabFilterPopover';
import TabPanel from '@/components/TabPanel';
import TabSelect from '@/components/TabSelect';
import { areArraysOfObjectsEqual } from '@/helpers/comparators';
import { getFilterOptions } from '@/helpers/filters/filterOptions';
import { isRoleAllowedWhitelabelSelect } from '@/helpers/roles';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectMeRole } from '@/store/selectors/me.selectors';
import {
  selectSharedApiClients,
  selectSharedApiLoading,
  selectSharedApiWhitelabels,
} from '@/store/selectors/sharedApi.selectors';
import { setFilters } from '@/store/slices/creativeSlice';
import { getClients, getWhitelabels } from '@/store/slices/sharedApi.slice';
import { a11yProps } from '@/utils/tabProps';
import { Tab, Tabs, tabClasses } from '@mui/material';
import { MouseEventHandler, SyntheticEvent, useCallback, useEffect, useMemo, useState } from 'react';

export const BASE_TAB_ID = 'creatives-filter-tab';

const FilterPopover = () => {
  const dispatch = useAppDispatch();

  const { clients } = useAppSelector(selectSharedApiClients);
  const { whitelabels } = useAppSelector(selectSharedApiWhitelabels);
  const loadingFilterSource = useAppSelector(selectSharedApiLoading);
  const meRole = useAppSelector(selectMeRole);
  const filters = useAppSelector(({ creative }) => creative.filters);

  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [currentTab, setCurrentTab] = useState(FilterPopoverTabs.Clients);

  const [pendingWhitelabels, setPendingWhitelabels] = useState<IWhitelabelControlOption[]>(filters.whitelabels);
  const [pendingClients, setPendingClients] = useState<IClientControlOption[]>(filters.clients);
  const [pendingAdvertisers, setPendingAdvertisers] = useState<IControlOption[]>(filters.advertisers);

  const showWhitelabelLevel = useMemo<boolean>(() => isRoleAllowedWhitelabelSelect(meRole), [meRole]);

  useEffect(() => {
    if (showWhitelabelLevel) {
      dispatch(getWhitelabels());
    } else {
      dispatch(getClients());
    }
  }, [dispatch, showWhitelabelLevel]);

  const FILTER_TABS = useMemo(
    () =>
      showWhitelabelLevel
        ? [FilterPopoverTabs.Whitelabels, FilterPopoverTabs.Clients, FilterPopoverTabs.Advertisers]
        : [FilterPopoverTabs.Clients, FilterPopoverTabs.Advertisers],
    [showWhitelabelLevel],
  );

  const filtersOpen = useMemo(() => Boolean(filterAnchorEl), [filterAnchorEl]);
  const filtersId = useMemo(() => (filtersOpen ? 'filters-popover' : undefined), [filtersOpen]);

  const options = useMemo(() => {
    return getFilterOptions({
      userRole: meRole!,
      clients,
      selectedClients: pendingClients,
      selectedWhitelabels: pendingWhitelabels,
      whitelabels,
      options: {
        prefetchAdvertisers: true,
      },
    });
  }, [clients, meRole, pendingClients, pendingWhitelabels, whitelabels]);

  const commonTabPanelProps = useMemo(
    () => ({
      className: 'w-full',
      baseTabId: BASE_TAB_ID,
      value: currentTab,
    }),
    [currentTab],
  );

  const resetPendingValues = useCallback(() => {
    setPendingWhitelabels(filters.whitelabels);
    setPendingClients(filters.clients);
    setPendingAdvertisers(filters.advertisers);
  }, [filters.advertisers, filters.clients, filters.whitelabels]);

  const handleTabChange = useCallback((_: SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  }, []);

  const handleOpen = useCallback<MouseEventHandler<HTMLButtonElement>>(
    ({ currentTarget }) => {
      resetPendingValues();
      setFilterAnchorEl(currentTarget);
    },
    [resetPendingValues],
  );

  const handleClose = useCallback(() => {
    setFilterAnchorEl(null);
    resetPendingValues();
    setCurrentTab(FILTER_TABS[0]);
  }, [FILTER_TABS, resetPendingValues]);

  const handleSubmit = useCallback(() => {
    if (
      areArraysOfObjectsEqual(filters.whitelabels, pendingWhitelabels) &&
      areArraysOfObjectsEqual(filters.clients, pendingClients) &&
      areArraysOfObjectsEqual(filters.advertisers, pendingAdvertisers)
    ) {
      handleClose();
      return;
    }
    dispatch(
      setFilters({
        whitelabels: pendingWhitelabels,
        clients: pendingClients,
        advertisers: pendingAdvertisers,
      }),
    );
    handleClose();
  }, [
    filters.advertisers,
    filters.clients,
    filters.whitelabels,
    handleClose,
    pendingAdvertisers,
    pendingClients,
    pendingWhitelabels,
    dispatch,
  ]);

  const updateWhitelabelsValue = useCallback((updatedWhitelabels: IWhitelabelControlOption[] = []) => {
    setPendingWhitelabels(updatedWhitelabels);
    setPendingClients([]);
    setPendingAdvertisers([]);
  }, []);

  const updateClientsValue = useCallback((updatedClients: IClientControlOption[] = []) => {
    setPendingClients(updatedClients);
    setPendingAdvertisers([]);
  }, []);

  const updateAdvertisersValue = useCallback((updatedAdvertisers: IControlOption[] = []) => {
    setPendingAdvertisers(updatedAdvertisers);
  }, []);

  useEffect(() => {
    setCurrentTab(FILTER_TABS[0]);
  }, [FILTER_TABS]);

  return (
    <>
      <AddFilterButton
        onClick={handleOpen}
        describedBy={filtersId}
        variant='outlined'
        sx={{ height: '40px', whiteSpace: 'nowrap' }}
      />
      <TabFilterPopover
        id={filtersId}
        open={filtersOpen}
        anchorEl={filterAnchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Tabs
          value={currentTab}
          orientation='vertical'
          onChange={handleTabChange}
          aria-label='pixels filter tabs'
          sx={{
            minHeight: 'unset',
            background: '#1B43320A',
            padding: '16px',
            minWidth: '230px',
            [`& .${tabClasses.root}`]: {
              paddingX: 0,
              minHeight: 'unset',
              justifyContent: 'flex-start',
              paddingLeft: '16px',
              paddingRight: '16px',
            },
            [`& .${tabClasses.selected}`]: {
              background: '#1B43321F',
            },
          }}
          slotProps={{
            indicator: {
              hidden: true,
            },
          }}
        >
          {showWhitelabelLevel ? (
            <Tab
              value={FilterPopoverTabs.Whitelabels}
              component={WhitelabelsTabButton}
              {...a11yProps(FilterPopoverTabs.Whitelabels, BASE_TAB_ID)}
            />
          ) : null}
          <Tab
            value={FilterPopoverTabs.Clients}
            component={ClientsTabButton}
            {...a11yProps(FilterPopoverTabs.Clients, BASE_TAB_ID)}
          />
          <Tab
            value={FilterPopoverTabs.Advertisers}
            component={AdvertisersTabButton}
            {...a11yProps(FilterPopoverTabs.Advertisers, BASE_TAB_ID)}
          />
        </Tabs>
        {showWhitelabelLevel ? (
          <TabPanel {...commonTabPanelProps} index={FilterPopoverTabs.Whitelabels}>
            <TabSelect
              label='Whitelabels'
              loading={loadingFilterSource}
              options={options.whitelabel}
              value={pendingWhitelabels}
              onChange={updateWhitelabelsValue}
              onClose={handleClose}
              onSubmit={handleSubmit}
            />
          </TabPanel>
        ) : null}
        <TabPanel {...commonTabPanelProps} index={FilterPopoverTabs.Clients}>
          <TabSelect
            label='Client'
            loading={loadingFilterSource}
            options={options.client}
            value={pendingClients}
            onChange={updateClientsValue}
            onClose={handleClose}
            onSubmit={handleSubmit}
          />
        </TabPanel>
        <TabPanel {...commonTabPanelProps} index={FilterPopoverTabs.Advertisers}>
          <TabSelect
            label='Advertiser'
            loading={loadingFilterSource}
            options={options.advertiser}
            value={pendingAdvertisers}
            onChange={updateAdvertisersValue}
            onClose={handleClose}
            onSubmit={handleSubmit}
          />
        </TabPanel>
      </TabFilterPopover>
    </>
  );
};

export default FilterPopover;
